import React, { useEffect, useState } from "react";
import api from "../../services/api"
import { endpoints } from "../../services/endpoints";
import { useNavigate } from "react-router-dom";
import { UserCircle, Lock as LockIcon } from "lucide-react";
import { isValidId, toNum, permMatchesUser, normUser, normLockFromList, normLockDetail, mapInBatches } from "../../utils/format"

export default function UserSupervisor() {
  const navigate = useNavigate();

  const [company, setCompany] = useState({
    id: null,
    name: "—",
    resolving: true,
    error: null,
  });

  const [rows, setRows] = useState([]); // [{ user, padlocks }]
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Resolver empresa del supervisor (localStorage → GET /api/users/:id)
  useEffect(() => {
    let alive = true;
    async function resolveCompany() {
      try {
        const raw = localStorage.getItem("user");
        const u = raw ? JSON.parse(raw) : null;

        const localCompanyObj = u && u.company ? u.company : null;
        const localCompanyId =
          (localCompanyObj && toNum(localCompanyObj.id)) ??
          toNum(u && u.companyId) ??
          toNum(u && u.company_id);
        const localCompanyName =
          (localCompanyObj && (localCompanyObj.name ?? localCompanyObj.companyName ?? localCompanyObj.company_name)) ||
          null;

        if (localCompanyId !== null) {
          if (alive) {
            setCompany({
              id: localCompanyId,
              name: localCompanyName || "—",
              resolving: false,
              error: null,
            });
          }
          return;
        }

        // fallback: /users/:id
        const uid =
          (u && toNum(u.id)) ??
          (u && toNum(u.userId)) ??
          (u && toNum(u.user_id));
        if (uid === null) {
          if (alive) setCompany({ id: null, name: "—", resolving: false, error: "Usuario inválido" });
          return;
        }

        const r = await api.get(endpoints.users.byId(uid));
        const data = (r.data && (r.data.data ?? r.data)) || {};
        const comp = data.company ?? null;

        const apiCompanyId =
          (comp && toNum(comp.id)) ??
          toNum(data.companyId) ??
          toNum(data.company_id);
        const apiCompanyName =
          (comp && (comp.name ?? comp.companyName ?? comp.company_name)) ||
          null;

        if (alive) {
          setCompany({
            id: apiCompanyId,
            name: apiCompanyName || "—",
            resolving: false,
            error: null,
          });
        }
      } catch (e) {
        if (alive) setCompany({ id: null, name: "—", resolving: false, error: String(e) });
      }
    }
    resolveCompany();
    return () => { alive = false; };
  }, []);

  // Carga de usuarios + candados (filtrando por empresa del supervisor)
  useEffect(() => {
    if (company.resolving) return; // espera a tener empresa
    let alive = true;

    async function load() {
      setLoading(true);
      setErr(null);
      try {
        // 1) Usuarios
        let usersRes;
        try {
          usersRes = await api.get(endpoints.users.list);
        } catch (_e) {
          usersRes = { data: [] };
        }
        const rawUsers = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data && (usersRes.data.data ?? [])) || [];
        const allUsers = rawUsers.map(normUser);
        const companyUsers = company.id !== null
          ? allUsers.filter(u => u.companyId === company.id)
          : [];

        // 2) Candados (lista base)
        let padlocksRes;
        try {
          padlocksRes = await api.get(endpoints.padlock.list);
        } catch (_e) {
          padlocksRes = { data: [] };
        }
        const rawPadlocks = Array.isArray(padlocksRes.data) ? padlocksRes.data : (padlocksRes.data && (padlocksRes.data.data ?? [])) || [];
        const listPadlocks = rawPadlocks.map(normLockFromList).filter(p => isValidId(p.id));

        // 3) Detalles /padlock/:id (para permisos y empresa)
        const details = await mapInBatches(listPadlocks, 8, async (p) => {
          if (!isValidId(p.id)) return null;
          try {
            const r = await api.get(endpoints.padlock.byId(p.id));
            const payload = (r.data && (r.data.data ?? r.data)) || {};
            return normLockDetail(payload);
          } catch {
            return null;
          }
        });
        const fullPadlocks = details.filter(Boolean);

        // 4) Filtra candados por empresa del supervisor
        const companyPadlocks = company.id !== null
          ? fullPadlocks.filter(pl => pl.companyId === company.id)
          : [];

        // 5) Cruce permisos usuario ↔ candado
        const built = companyUsers.map((usr) => {
          const allowed = companyPadlocks.filter(pl =>
            Array.isArray(pl.permissions) && pl.permissions.some(perm => permMatchesUser(perm, usr))
          );

          return {
            user: usr,
            padlocks: allowed.map(p => ({ id: p.id, name: p.name, serialNumber: p.serialNumber })),
          };
        });

        if (alive) setRows(built);
      } catch (e) {
        if (alive) setErr(e);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => { alive = false; };
  }, [company]);

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold">Usuarios Supervisados</h1>

      {!company.resolving && company.id === null && (
        <div className="p-3 rounded bg-yellow-50 text-yellow-800 border border-yellow-200">
          Tu usuario no tiene empresa asignada. No se muestran usuarios.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border rounded shadow">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left">Nombre</th>
              <th className="px-4 py-2 text-left">DNI</th>
              <th className="px-4 py-2 text-left">Rol</th>
              <th className="px-4 py-2 text-left">Candados autorizados</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-4 py-6 text-gray-500">Cargando…</td></tr>
            )}

            {err && !loading && (
              <tr><td colSpan={4} className="px-4 py-6 text-red-600">
                Error al cargar: {String(err)}
              </td></tr>
            )}

            {!loading && !err && company.id !== null && rows.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-gray-500">No se encontraron usuarios.</td></tr>
            )}

            {!loading && !err && rows.map(({ user, padlocks }) => (
              <tr key={user.id} className="border-t hover:bg-gray-50 align-top">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <UserCircle size={18} className="text-gray-500" />
                    <span className="font-medium">{user.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3">{user.dni}</td>
                <td className="px-4 py-3">{user.role}</td>
                <td className="px-4 py-3">
                  {padlocks.length === 0 ? (
                    <span className="text-gray-400">Sin permisos</span>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {padlocks.map((p) => {
                        const canNavigate = !!p.serialNumber;
                        return (
                          <button
                            key={`${user.id}-${p.id ?? p.serialNumber}`}
                            onClick={() => {
                              if (canNavigate) {
                                // Detalle de candado por serial para supervisores
                                navigate(`/padlocks/${encodeURIComponent(p.serialNumber)}`);
                              }
                            }}
                            className={`flex items-center gap-1 px-2 py-1 rounded border text-xs ${
                              canNavigate
                                ? "hover:bg-blue-50 text-blue-700 border-blue-200"
                                : "text-gray-500 border-gray-200 cursor-not-allowed"
                            }`}
                            disabled={!canNavigate}
                            title={canNavigate ? "Ver candado" : "No hay serial disponible"}
                          >
                            <LockIcon size={14} />
                            <span className="font-medium">{p.name}</span>
                            <span className="text-gray-500">({p.serialNumber ?? "s/n"})</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}